import { CommonModule } from "@angular/common";
import { Component, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  lettersOnlyValidator,
  tcKimlikValidator,
} from "../../validators/validators";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import {
  AppoinmentTypeModel,
  ResourceModel,
} from "../../models/appointment/appoinment.model";
import { RecaptchaModule, RecaptchaFormsModule } from "ng-recaptcha-2";
import { environment } from "../../../environments/environment";
import {
  RECAPTCHA_LOADER_OPTIONS,
  RECAPTCHA_SETTINGS,
  RecaptchaLoaderOptions,
  RecaptchaSettings,
} from "ng-recaptcha-2";
import { dateFormatValidator } from "../../validators/birth-date-validator.validator";
import { AREACODES } from "../../constants/area-codes";
import { NgSelectModule } from "@ng-select/ng-select";
import { HttpClient } from "@angular/common/http";
import { ToastService } from "../../shared/services/toast.service";
import { LocaleService } from "../../shared/i18n/locale.service";
import { TranslatePipe } from "../../shared/i18n/translate.pipe";

type PreparationFormState = {
  name: string;
  lastName: string;
  tc: string;
  birthDate: string;
  gender: number | "";
  phone: string;
  email: string;
  city: string | null;
  district: string | null;
  neighborhood: string;
  street: string;
  buildingNo: string;
  apartmentNo: string;
};

type CheckPatientByPidNoResponse = {
  Exists: boolean;
  Name?: string | null;
  Surname?: string | null;
  Email?: string | null;
  Id?: string | null;
};

function recaptchaSettingsFactory(
): RecaptchaSettings {
  return {
    siteKey: environment.siteKey,
  };
}

function recaptchaLoaderOptionsFactory(
  localeService: LocaleService,
): RecaptchaLoaderOptions {
  return {
    onBeforeLoad(url) {
      url.searchParams.set("hl", localeService.getRecaptchaLanguage());
      return { url };
    },
  };
}

@Component({
  selector: "app-preparation",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    RouterModule,
    TranslatePipe,

    RecaptchaModule,
    RecaptchaFormsModule,
  ],
  templateUrl: "./preparation.component.html",
  styleUrl: "./preparation.component.scss",
  providers: [
    {
      provide: RECAPTCHA_SETTINGS,
      useFactory: recaptchaSettingsFactory,
    },
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: recaptchaLoaderOptionsFactory,
      deps: [LocaleService],
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PreparationComponent {
  private readonly preparationStorageKey = "preparationFormData";
  maxDate: string;
  preparationForm: FormGroup;
  date: string = "";
  time: string = "";
  formattedDateTime: string = "";
  applicationType: string = "Custom";
  appoinmentType: AppoinmentTypeModel = {
    id: "",
    name: "",
  };
  resource: ResourceModel = {
    id: "",
    name: "",
    customerId: "",
    languageCode: "",
  };
  selectedDate: string = "";
  selectedTime: string = "";
  submitLoading: boolean = false;
  captchaResolved: boolean = false;
  captchaToken: string | null = null;
  siteKey = environment.siteKey;
  timeDifference: string = "+0";
  countries: { code: string; name: string; shortCode: string }[] = AREACODES;
  cities: { id: string; name: string }[] = [];
  districts: { id: string; name: string }[] = [];
  patientId: string | null = null;
  isPatientIdentityLocked: boolean = false;
  private lastCheckedPidNo: string | null = null;
  formState: PreparationFormState = {
    name: "",
    lastName: "",
    tc: "",
    birthDate: "",
    gender: "",
    phone: "",
    email: "",
    city: null,
    district: null,
    neighborhood: "",
    street: "",
    buildingNo: "",
    apartmentNo: "",
  };
  requiredChecklist: { key: keyof PreparationFormState; labelKey: string }[] = [
    { key: "name", labelKey: "preparation.fieldLabels.name" },
    { key: "lastName", labelKey: "preparation.fieldLabels.lastName" },
    { key: "tc", labelKey: "preparation.fieldLabels.tc" },
    { key: "birthDate", labelKey: "preparation.fieldLabels.birthDate" },
    { key: "gender", labelKey: "preparation.fieldLabels.gender" },
    { key: "phone", labelKey: "preparation.fieldLabels.phone" },
    { key: "email", labelKey: "preparation.fieldLabels.email" },
    { key: "city", labelKey: "preparation.fieldLabels.city" },
    { key: "district", labelKey: "preparation.fieldLabels.district" },
    { key: "neighborhood", labelKey: "preparation.fieldLabels.neighborhood" },
    { key: "street", labelKey: "preparation.fieldLabels.street" },
    { key: "buildingNo", labelKey: "preparation.fieldLabels.buildingNo" },
    { key: "apartmentNo", labelKey: "preparation.fieldLabels.apartmentNo" },
  ];
  touchedFields: Partial<Record<keyof PreparationFormState, boolean>> = {};

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private toastService: ToastService,
    private localeService: LocaleService,
  ) {
    const today = new Date();
    this.maxDate = today.toISOString().split("T")[0];
    this.preparationForm = this.fb.group({
      gender: [null, [Validators.required]],
      name: ["", [Validators.required, lettersOnlyValidator()]],
      lastName: ["", [Validators.required, lettersOnlyValidator()]],
      tc: ["", [Validators.required]],
      email: ["", [Validators.required, Validators.email]],
      birthDate: ["", [Validators.required, dateFormatValidator()]],
      mobilePhone: [
        "",
        [Validators.required, Validators.pattern("^\\+?[0-9]{7,15}$")],
      ],
      address: [""],
      houseNumber: ["", []],
      place: [""],
      comment: [""],
      consent: [false],
      areaCode: [null],
    });
  }

  ngOnInit(): void {
    this.syncIdentityValidators();
    this.loadCities();
    this.appoinmentType = history.state.appoinmentType;
    this.resource = history.state.resource;
    this.selectedDate = history.state.selectedDate;
    this.applicationType = history.state.applicationType;
    this.selectedTime = history.state.selectedTime;
    this.timeDifference = history.state.timeDifference;
    this.applyPidFromQuery();
    if (this.selectedDate && this.selectedTime) {
      this.formattedDateTime = this.formatDateTime(
        this.selectedDate,
        this.selectedTime,
      );
    }
  }

  private readonly patientApiBase = environment.apiLocations.patient;

  private normalizeList<T>(response: any): T[] {
    if (Array.isArray(response)) return response as T[];
    if (Array.isArray(response?.data)) return response.data as T[];
    if (Array.isArray(response?.Data)) return response.Data as T[];
    return [];
  }

  loadCities(): void {
    this.http
      .get<any>(`${this.patientApiBase}/countries/countries/225/states`)
      .subscribe({
        next: (res) => {
          const list = this.normalizeList<{ id: number; name: string }>(res);
          this.cities = list.map((item) => ({
            id: String(item.id),
            name: item.name,
          }));
        },
        error: () => {
          this.cities = [];
        },
      });
  }

  onCityChange(cityId: string | null): void {
    this.formState.city = cityId ?? null;
    this.formState.district = null;
    this.districts = [];
    if (!cityId) return;

    this.http
      .get<any>(`${this.patientApiBase}/countries/states/${cityId}/cities`)
      .subscribe({
        next: (res) => {
          const list = this.normalizeList<{ id: number; name: string }>(res);
          this.districts = list.map((item) => ({
            id: String(item.id),
            name: item.name,
          }));
        },
        error: () => {
          this.districts = [];
        },
      });
  }

  formatDateTime(date: string, time: string): string {
    const [year, month, day] = date.split("-").map(Number);
    const [hours, minutes] = time.split(":").map(Number);

    const dateTime = new Date(year, month - 1, day, hours, minutes);

    return this.localeService.formatDate(dateTime, {
      weekday: "short",
      day: "numeric",
      month: "long",
      hour: "numeric",
      minute: "numeric",
    });
  }

  isControlInvalid(controlName: string): boolean {
    const control = this.preparationForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onCaptchaResolved(token: string | null): void {
    this.captchaToken = token;
    this.captchaResolved = !!token;
  }

  isRequiredMissing(key: keyof PreparationFormState): boolean {
    const value = this.formState[key];
    if (typeof value === "number") return false;
    return String(value ?? "").trim().length === 0;
  }

  isFieldTouched(key: keyof PreparationFormState): boolean {
    return !!this.touchedFields[key];
  }

  markTouched(key: keyof PreparationFormState): void {
    this.touchedFields[key] = true;
  }

  isEmailValid(email: string): boolean {
    const value = String(email ?? "").trim();
    if (!value) return false;
    return Validators.email(new FormControl(value)) === null;
  }

  get isSubmitDisabled(): boolean {
    // Required alanlar kontrolü
    const missingRequired = this.requiredChecklist.some((item) =>
      this.isRequiredMissing(item.key),
    );

    // Email kontrolü - sadece dolu ise geçerlilik kontrolü yap
    const emailInvalid =
      !this.isRequiredMissing("email") &&
      !this.isEmailValid(this.formState.email);

    // Kimlik alanı kontrolü - sadece TR locale'de doğrulama yap
    const tcControl = this.preparationForm.get("tc");
    const tcInvalid =
      this.isTurkishIdentityMode &&
      !this.isRequiredMissing("tc") &&
      tcControl?.errors?.["tcKimlikInvalid"];

    // Doğum tarihi kontrolü - sadece dolu ise geçerlilik kontrolü yap
    const birthDateControl = this.preparationForm.get("birthDate");
    const birthDateInvalid =
      !this.isRequiredMissing("birthDate") &&
      (birthDateControl?.errors?.["dateFormatInvalid"] ||
        birthDateControl?.errors?.["dateOfBirthMinLength"]);

    return missingRequired || emailInvalid || tcInvalid || birthDateInvalid;
  }

  get hasAnyTouched(): boolean {
    const anyTemplateTouched = Object.values(this.touchedFields).some(Boolean);
    const tcTouched = !!this.preparationForm.get("tc")?.touched;
    const birthDateTouched = !!this.preparationForm.get("birthDate")?.touched;
    const anyValueEntered = this.requiredChecklist.some(
      (item) => !this.isRequiredMissing(item.key),
    );
    return (
      anyTemplateTouched || tcTouched || birthDateTouched || anyValueEntered
    );
  }

  get missingFieldLabels(): string[] {
    const missing = this.requiredChecklist
      .filter((item) => this.isRequiredMissing(item.key))
      .map((item) => this.localeService.t(item.labelKey));

    if (
      !this.isRequiredMissing("email") &&
      !this.isEmailValid(this.formState.email)
    ) {
      missing.push(this.localeService.t("preparation.invalidEmailLabel"));
    }

    if (
      this.isTurkishIdentityMode &&
      this.preparationForm.get("tc")?.errors?.["tcKimlikInvalid"]
    ) {
      missing.push(this.localeService.t("preparation.tcInvalid"));
    }

    if (
      this.preparationForm.get("birthDate")?.errors?.["dateFormatInvalid"] ||
      this.preparationForm.get("birthDate")?.errors?.["dateOfBirthMinLength"]
    ) {
      missing.push(this.localeService.t("preparation.invalidBirthDateLabel"));
    }

    return missing;
  }

  onNameInput(value: string): void {
    const sanitized = value.replace(/[^a-zA-ZçÇğĞıİöÖşŞüÜ\s]/g, "");
    this.formState.name = sanitized;
    this.preparationForm.patchValue({ name: sanitized });
  }

  onLastNameInput(value: string): void {
    const sanitized = value.replace(/[^a-zA-ZçÇğĞıİöÖşŞüÜ\s]/g, "");
    this.formState.lastName = sanitized;
    this.preparationForm.patchValue({ lastName: sanitized });
  }

  onNameKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
      " ",
    ];

    if (allowedKeys.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return;
    if (!/^[a-zA-ZçÇğĞıİöÖşŞüÜ]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onEmailInput(value: string): void {
    let sanitized = value.replace(/[^a-zA-Z0-9@._+-]/g, "");
    sanitized = sanitized.replace(/^\d+/, "");
    this.formState.email = sanitized;
    this.preparationForm.patchValue({ email: sanitized });
  }

  onEmailKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return;

    const input = event.target as HTMLInputElement | null;
    const currentValue = input?.value ?? "";
    const start = input?.selectionStart ?? currentValue.length;
    const end = input?.selectionEnd ?? currentValue.length;
    const nextValue =
      currentValue.slice(0, start) + event.key + currentValue.slice(end);

    if (/^\d/.test(nextValue)) {
      event.preventDefault();
      return;
    }

    if (!/^[a-zA-Z0-9@._+-]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTcInput(value: string): void {
    const normalized = this.normalizeIdentityValue(value);
    this.formState.tc = normalized;

    const tcControl = this.preparationForm.get("tc");
    if (tcControl?.value !== normalized) {
      tcControl?.patchValue(normalized, { emitEvent: false });
    }

    if (!normalized) {
      this.resetPatientLookup();
      return;
    }

    if (!this.isTurkishIdentityMode) {
      if (
        this.lastCheckedPidNo === normalized &&
        (this.patientId !== null || this.isPatientIdentityLocked)
      ) {
        return;
      }
      this.resetPatientLookup();
      return;
    }

    if (normalized.length !== 11) {
      this.resetPatientLookup();
      return;
    }

    const isValidTc = !tcControl?.errors?.["tcKimlikInvalid"];
    if (!isValidTc) {
      this.resetPatientLookup();
      return;
    }

    if (this.lastCheckedPidNo === normalized) return;
    this.lastCheckedPidNo = normalized;
    this.checkPatientByPidNo(normalized);
  }

  onIdentityBlur(): void {
    const tcControl = this.preparationForm.get("tc");
    tcControl?.markAsTouched();

    const normalized = this.normalizeIdentityValue(
      String(tcControl?.value ?? this.formState.tc ?? ""),
    );
    this.formState.tc = normalized;
    if (tcControl?.value !== normalized) {
      tcControl?.patchValue(normalized, { emitEvent: false });
      tcControl?.updateValueAndValidity({ emitEvent: false });
    }

    if (!normalized || this.lastCheckedPidNo === normalized) {
      return;
    }

    if (this.isTurkishIdentityMode) {
      const isValidTc = !tcControl?.errors?.["tcKimlikInvalid"];
      if (normalized.length !== 11 || !isValidTc) {
        return;
      }
    }

    this.lastCheckedPidNo = normalized;
    this.checkPatientByPidNo(normalized);
  }

  onGenderChange(value: number | ""): void {
    this.formState.gender = value;
    this.preparationForm.patchValue({ gender: value });
  }

  get isIdentityInputDisabled(): boolean {
    const tcValue = String(this.formState.tc ?? "").trim();
    return !tcValue || this.isPatientIdentityLocked;
  }

  get isTurkishIdentityMode(): boolean {
    return this.localeService.getCurrentLocale() === "tr";
  }

  private checkPatientByPidNo(pidNo: string): void {
    if (!pidNo) return;
    this.http
      .get<CheckPatientByPidNoResponse>(
        `${this.patientApiBase}/patients/by-pid/${pidNo}`,
      )
      .subscribe({
        next: (res: any) => {
          const data = res?.data ?? res;
          const id = data?.id ?? data?.Id ?? null;
          const firstName = data?.name ?? data?.Name ?? null;
          const lastName =
            data?.surname ?? data?.Surname ?? data?.lastName ?? data?.LastName;
          const hasPatient =
            !!id || res?.Exists === true || data?.Exists === true;

          if (hasPatient) {
            this.patientId = id ? String(id) : null;
            if (firstName) this.onNameInput(String(firstName));
            if (lastName) this.onLastNameInput(String(lastName));
            this.isPatientIdentityLocked = !!(firstName || lastName);
          } else {
            this.patientId = null;
            this.isPatientIdentityLocked = false;
          }
        },
        error: () => {
          this.patientId = null;
          this.isPatientIdentityLocked = false;
        },
      });
  }

  async savePreparation(e: Event) {
    e.preventDefault();
    if (!this.isSubmitDisabled) {
      if (this.captchaResolved) {
        this.submitLoading = true;
        const [day, month, year] = this.formState.birthDate
          .split("-")
          .map(Number);
        const dateOfBirth = `${year}-${String(month).padStart(2, "0")}-${String(
          day,
        ).padStart(2, "0")}`;

        const phoneNumber = this.preparationForm.value.areaCode
          ? `+${this.preparationForm.value.areaCode}${this.preparationForm.value.mobilePhone}`
          : this.preparationForm.value.mobilePhone;

        const formData = {
          selectedDateTime: this.formatSelectedDateTime(
            this.selectedDate,
            this.selectedTime,
          ),
          customerBranchId: this.resource.id,
          customerBranchName: this.resource.name,
          customerBranchLanguageCode: this.resource.languageCode,
          appointmentTypeId: this.appoinmentType.id,
          applicationType: this.applicationType,
          customerCode: "HUID",
          timeDifference: this.timeDifference,
          FirstName: this.formState.name,
          LastName: this.formState.lastName,
          TcKimlikNo: this.formState.tc,
          DateOfBirth: dateOfBirth,
          Gender: this.formState.gender || 0,
          PhoneNumber: phoneNumber || null,
          Email: this.formState.email || null,
          City: this.formState.city || null,
          District: this.formState.district || null,
          Neighborhood: this.formState.neighborhood || null,
          Street: this.formState.street || null,
          BuildingNo: this.formState.buildingNo || null,
          ApartmentNo: this.formState.apartmentNo || null,
        };
      }
    } else {
      this.preparationForm.markAllAsTouched();
    }
  }

  goToCreateAppointment(): void {
    const [day, month, year] = this.formState.birthDate.split("-").map(Number);
    const dateOfBirth = `${year}-${String(month).padStart(2, "0")}-${String(
      day,
    ).padStart(2, "0")}`;
    const phoneNumber = this.preparationForm.value.areaCode
      ? `+${this.preparationForm.value.areaCode}${this.preparationForm.value.mobilePhone}`
      : this.preparationForm.value.mobilePhone;

    const payload = {
      PatientId: this.patientId ?? null,
      FirstName: this.formState.name,
      LastName: this.formState.lastName,
      TcKimlikNo: this.formState.tc,
      DateOfBirth: dateOfBirth,
      Gender: this.formState.gender || 0,
      PhoneNumber: phoneNumber || null,
      Email: this.formState.email || null,
      City: this.formState.city || null,
      District: this.formState.district || null,
      Neighborhood: this.formState.neighborhood || null,
      Street: this.formState.street || null,
      BuildingNo: this.formState.buildingNo || null,
      ApartmentNo: this.formState.apartmentNo || null,
      selectedDate: this.selectedDate,
      selectedTime: this.selectedTime,
      applicationType: this.applicationType,
    };
    localStorage.setItem(this.preparationStorageKey, JSON.stringify(payload));
    this.router.navigate(["/create-appoinment"], {
      queryParamsHandling: "preserve",
    });
  }

  formatSelectedDateTime(date: string, time: string): string {
    const dateTimeString = `${date}T${time}:00`;
    const dateTime = new Date(dateTimeString);

    const timezoneOffset = dateTime.getTimezoneOffset() * 60000; //Convert minutes to milliseconds time difference
    const localISOTime = new Date(dateTime.getTime() - timezoneOffset)
      .toISOString()
      .slice(0, -1); //Remove the trailing "Z" from the ISO string, which indicates UTC time

    return localISOTime;
  }

  displayToast(show: boolean, message: string, type: string | any) {
    if (!show) {
      this.toastService.hideToast();
      return;
    }
    this.toastService.showToast(message, type);
  }

  checkAndRenderCaptcha() {
    if (typeof window.grecaptcha !== "undefined") {
      window.grecaptcha.render("recaptcha-container", {
        sitekey: this.siteKey,
      });
    } else {
      setTimeout(() => this.checkAndRenderCaptcha(), 100);
    }
  }

  onDateInputChange(value: string): void {
    const sanitizedValue = value.replace(/[^0-9]/g, "");
    const today = new Date();
    let formattedValue = "";

    if (sanitizedValue.length <= 2) {
      formattedValue = sanitizedValue;
    } else if (sanitizedValue.length <= 4) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2,
      )}`;
    } else if (sanitizedValue.length <= 8) {
      formattedValue = `${sanitizedValue.slice(0, 2)}-${sanitizedValue.slice(
        2,
        4,
      )}-${sanitizedValue.slice(4)}`;
    }

    const [day, month, year] = formattedValue
      .split("-")
      .map((part) => parseInt(part, 10));

    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 100;
    if (
      formattedValue.startsWith("00") ||
      formattedValue.substring(3, 5) === "00" ||
      formattedValue.substring(6, 7) === "0" ||
      (day && day > 31) ||
      (month && month > 12) ||
      (year &&
        year.toString().length == 4 &&
        (year > currentYear || year < minYear))
    ) {
      this.formState.birthDate = "";
      this.preparationForm.patchValue({
        birthDate: "",
      });
      return;
    }

    const inputDate = new Date(year, month - 1, day);

    const todayFormatted = `${today.getDate().toString().padStart(2, "0")}-${(
      today.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}-${today.getFullYear()}`;

    const finalValue =
      sanitizedValue.length === 8 && inputDate > today
        ? todayFormatted
        : formattedValue;

    this.formState.birthDate = finalValue;
    this.preparationForm.patchValue({
      birthDate: finalValue,
    });
  }

  onPhoneInputChange(value: string): void {
    let digits = value.replace(/[^0-9]/g, "");
    if (digits.startsWith("0")) {
      digits = digits.replace(/^0+/, "");
    }
    digits = digits.slice(0, 10);
    let formatted = "";

    if (digits.length <= 3) {
      formatted = digits;
    } else if (digits.length <= 6) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
    } else if (digits.length <= 8) {
      formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(
        6,
      )}`;
    } else {
      formatted = `${digits.slice(0, 3)} ${digits.slice(
        3,
        6,
      )} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
    }

    this.formState.phone = formatted;
    this.preparationForm.patchValue({
      mobilePhone: digits,
    });
  }

  onPhoneKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      "ArrowLeft",
      "ArrowRight",
      "Home",
      "End",
    ];

    if (allowedKeys.includes(event.key)) return;
    if (event.ctrlKey || event.metaKey) return;
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  isChecklistDone(key: keyof PreparationFormState): boolean {
    const value = this.formState[key];
    if (typeof value === "number") return true;
    if (typeof value !== "string") return false;
    return value.trim().length > 0;
  }

  get completedChecklist(): {
    key: keyof PreparationFormState;
    label: string;
  }[] {
    return this.requiredChecklist
      .filter((item) => this.isChecklistDone(item.key))
      .map((item) => ({
        key: item.key,
        label: this.localeService.t(item.labelKey),
      }));
  }

  private syncIdentityValidators(): void {
    const tcControl = this.preparationForm.get("tc");
    if (!tcControl) return;

    const validators = this.isTurkishIdentityMode
      ? [Validators.required, tcKimlikValidator()]
      : [Validators.required];

    tcControl.setValidators(validators);
    tcControl.updateValueAndValidity({ emitEvent: false });
  }

  private applyPidFromQuery(): void {
    const pid = this.route.snapshot.queryParamMap.get("pid");
    if (!pid) return;

    const normalized = this.normalizeIdentityValue(pid);
    if (!normalized) return;

    this.formState.tc = normalized;
    this.preparationForm.patchValue({ tc: normalized }, { emitEvent: false });
    this.preparationForm.get("tc")?.updateValueAndValidity({ emitEvent: false });

    if (this.isTurkishIdentityMode) {
      const tcControl = this.preparationForm.get("tc");
      const isValidTc = !tcControl?.errors?.["tcKimlikInvalid"];
      if (normalized.length !== 11 || !isValidTc) {
        return;
      }
    }

    this.lastCheckedPidNo = normalized;
    this.checkPatientByPidNo(normalized);
  }

  private normalizeIdentityValue(value: string): string {
    if (this.isTurkishIdentityMode) {
      return value.replace(/[^0-9]/g, "").slice(0, 11);
    }

    return value.trim().slice(0, 64);
  }

  private resetPatientLookup(clearLookupKey: boolean = true): void {
    this.patientId = null;
    this.isPatientIdentityLocked = false;

    if (clearLookupKey) {
      this.lastCheckedPidNo = null;
    }
  }
}
